import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  useRef
} from "react";
import { useMutation, useApolloClient, gql } from "@apollo/client";
import qs from "qs";
import { Capacitor } from "@capacitor/core";

import { useDispatch, useSelector } from "react-redux";

// utils
import { useScriptConverter } from "../languageHelpers";
import { AUTH_USER, CLEAN_AUTH } from "../../actions/type";
import { setRollbarUserInfo } from "../../loggers";
import { clearAllUserSessions } from "../healthCheckStorage";
import { MYBESTUSER, CLIENTID } from "../../config/environment";

/**
 * Build the MyBestUser OAuth authorize URL (same redirect_uri rules as LandingPage).
 * Used for re-login prompts that must not lose the current page state.
 */
export const buildMyBestUserAuthUrl = () => {
  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  const isTAApk = Capacitor.isNativePlatform();
  const isDevelopment = MYBESTUSER.includes("dev.mybestuser.com");

  let redirectUri;
  if (isLocalhost) {
    redirectUri = window.location.origin + "/mybestuser";
  } else if (isTAApk) {
    redirectUri = isDevelopment
      ? "https://dev.mybestta.com/mybestuser"
      : "https://mybestta.com/mybestuser";
  } else {
    redirectUri = window.location.origin + "/mybestuser";
  }

  return (
    `${MYBESTUSER}/authorize` +
    qs.stringify(
      {
        client_id: CLIENTID,
        response_type: "code",
        redirect_uri: redirectUri,
        state: "state",
        scope: "api.mybestta.com api.mybestuser.com"
      },
      { addQueryPrefix: true }
    )
  );
};

// components
import SignInDialog from "./SignInDialog";

export const useSignout = () => {
  const dispatch = useDispatch();

  const client = useApolloClient();
  return useCallback(async () => {
    // Clear health check sessions before removing user data
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user && user.id) {
          clearAllUserSessions(user.id);
        }
      }
    } catch (error) {
      console.error("[Signout] Error clearing health check sessions:", error);
    }

    //listen teacher use status
    localStorage.removeItem("token");
    localStorage.removeItem("time");
    localStorage.removeItem("isSendtoServer");
    localStorage.removeItem("expiresAt");
    localStorage.removeItem("tabletReturnPath");
    setRollbarUserInfo();

    sessionStorage.removeItem("mode");
    sessionStorage.removeItem("classroom");
    sessionStorage.removeItem("school");
    sessionStorage.removeItem("quizType");
    sessionStorage.removeItem("isTablet_healthCheck");
    sessionStorage.removeItem("tabletContext_student");
    sessionStorage.removeItem("activeBooksetId");
    sessionStorage.removeItem("isTablet_teachingAssistant_student");
    sessionStorage.removeItem("previousURL");

    // redux compatibility
    localStorage.removeItem("user");
    dispatch({ type: CLEAN_AUTH, payload: {} });

    client.writeQuery({ query: AUTH_CHECK, data: { me: null } });
    client.cache.data.clear();
    client.cache.optimisticData = client.cache.data;
    // client.clearStore();
  }, [client, dispatch]);
};

export const useSigninWithCode = () => {
  const dispatch = useDispatch();

  // language
  const converter = useScriptConverter();

  const [signinWithCode] = useMutation(SIGNIN_WITH_CODE, {
    update: (caches, { data: { signinWithCode } }) => {
      caches.writeQuery({
        query: AUTH_CHECK,
        data: signinWithCode
      });
    }
  });
  return useCallback(
    async (code) => {
      if (!code)
        throw new Error(
          converter({
            en: "Abnormal login, please try again",
            tw: "異常登入, 請重試一次",
            cn: "异常登入，请重试一次"
          })
        );
      const res = await signinWithCode({ variables: { code } });

      // Determine applicationId based on environment
      // Default to production unless explicitly in dev environment
      const isDevelopment =
        process.env.REACT_APP_MYBESTUSER?.includes("dev.mybestuser.com");
      const requiredApplicationId = isDevelopment
        ? "5f0d28497fbc36716ccae4d7" // Development ticket applicationId
        : "5f4cb4e09476351522ef8507"; // Production ticket applicationId (default)

      if (
        res.data.signinWithCode.me?.role === "student" &&
        !res.data.signinWithCode.me.tickets.some(
          (ticket) => ticket.applicationId === requiredApplicationId
        )
      ) {
        throw new Error(
          converter({
            en: `You do not have permission to log in to this system. Please contact your English consultant to open the authorization code.`,
            tw: `您沒有權限登錄此系統,請聯絡教務開啟授權碼`,
            cn: `您没有权限登录此系统,请联络教务开启授权码`
          })
        );
      }

      if (!res.data || !res.data?.signinWithCode.accessToken) {
        const errorMessage = {
          invalid: converter({
            en: `Invalid username or password`,
            tw: `帳號、密碼輸入錯誤`,
            cn: `帐号、密码输入错误`
          }),
          signinFail: converter({
            en: `Sign in Fail`,
            tw: `登入失敗`,
            cn: `登入失败`
          }),
          contractExpired: converter({
            en: `The school contract has expired, please contact your English consultant`,
            tw: `學校合約已到期，請洽詢您的專屬教務`,
            cn: `分校合约已到期，请洽询您的专属教务`
          }),
          noSchool: converter({
            en: `No school invitation code, please confirm whether you have entered the invitation code or contact your English consultant`,
            tw: `無學校邀請碼，請確認您是否輸入邀請碼或洽詢您的專屬教務`,
            cn: `无学校邀请码，请确认您是否输入邀请码或洽询您的专属教务`
          })
        };
        throw new Error(errorMessage[res.data?.signinWithCode.message]);
      }
      const expiresAt = Date.now() + 7 * 24 * 60 * 60 * 1000; //改成1個禮拜 7 * 24 * 60 * 60 * 1000 過期

      localStorage.setItem("expiresAt", expiresAt);
      localStorage.setItem("token", res.data?.signinWithCode.accessToken);
      // redux compatibility
      localStorage.setItem("user", JSON.stringify(res.data?.signinWithCode.me));
      setRollbarUserInfo(res.data?.signinWithCode.me);
      dispatch({
        type: AUTH_USER,
        payload: {
          user: res.data?.signinWithCode.me
        }
      });

      return res;
    },
    [signinWithCode, dispatch, converter]
  );
};

// gql
const AUTH_CHECK = gql`
  query TaWeb_AuthCheck {
    me {
      id
      username
      name
      email
      type
      roles
      role
      status
    }
  }
`;

const SIGNIN_WITH_CODE = gql`
  mutation Signin($code: String!) {
    signinWithCode(code: $code) {
      accessToken
      me {
        id
        username
        name
        email
        type
        roles
        role
        status
        canEdit
        wordLevelProgress
        tickets {
          id
          name
          applicationId
        }
      }
      message
    }
  }
`;

export const MyBestUserSigninContext = createContext();

export const MyBestUserSigninProvider = ({ children }) => {
  const [open, setOpen] = useState(null);
  const onClose = useCallback(() => setOpen(null), []);

  const signin = useCallback((src) => setOpen(src), []);

  const dispatch = useDispatch();
  const client = useApolloClient();

  // Keep the currently signed-in user id in a ref so the (stable) message
  // handler below always sees the latest value without re-subscribing.
  const currentUserId = useSelector((state) => state.auth.user?.id);
  const currentUserIdRef = useRef(currentUserId);
  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  // Check if the URL contains isTablet=true parameter
  const isTablet = open ? open.includes("isTablet=true") : false;

  // Listen for postMessage from iframe (MyBestUserLogin)
  useEffect(() => {
    /**
     * In-place re-auth: the iframe (same origin) has already written the new
     * token/user into localStorage via signinWithCode. If the page is still
     * mounted with the SAME user, just sync Redux/Apollo and keep the page
     * (e.g. an in-progress test) alive instead of doing a full reload.
     * Returns true when handled in place.
     */
    const syncAuthInPlace = () => {
      try {
        const token = localStorage.getItem("token");
        const userStr = localStorage.getItem("user");
        if (!token || !userStr) return false;

        const user = JSON.parse(userStr);
        if (!user?.id || !currentUserIdRef.current) return false;
        if (user.id !== currentUserIdRef.current) return false; // different account → full reload

        if (!Array.isArray(user.roles)) user.roles = [];

        setRollbarUserInfo(user);
        dispatch({ type: AUTH_USER, payload: { user } });
        client.writeQuery({
          query: AUTH_CHECK,
          data: {
            me: {
              id: user.id,
              username: user.username ?? null,
              name: user.name ?? null,
              email: user.email ?? null,
              type: user.type ?? null,
              roles: user.roles,
              role: user.role ?? null,
              status: user.status ?? null
            }
          }
        });
        return true;
      } catch (error) {
        console.error("[MyBestUserSignin] in-place re-auth failed:", error);
        return false;
      }
    };

    const handleMessage = (event) => {
      if (event.data && event.data.type === "MYBESTUSER_LOGIN_DIAGNOSTIC") {
        const { redirectTarget } = event.data.data || {};

        // Close the dialog
        setOpen(null);

        // Same user re-logged in (token expiry / 401 prompt): stay on the page
        if (syncAuthInPlace()) return;

        // Navigate to the redirect target
        if (redirectTarget) {
          window.location.href = redirectTarget;
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [dispatch, client]);

  return (
    <MyBestUserSigninContext.Provider value={signin}>
      {children}
      <SignInDialog src={open} onClose={onClose} isTablet={isTablet} />
    </MyBestUserSigninContext.Provider>
  );
};

export const useMyBestUserSignin = () => useContext(MyBestUserSigninContext);
